/**
 * ReportExportButtons Component
 *
 * Export buttons for Print to PDF and Export to Excel.
 * Disabled when no report results are available.
 */

import React from 'react';
import { PrinterIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface ReportExportButtonsProps {
  onPrintPDF: () => void;
  onExportExcel: () => void;
  disabled?: boolean;
  isExporting?: boolean;
}

export const ReportExportButtons: React.FC<ReportExportButtonsProps> = ({
  onPrintPDF,
  onExportExcel,
  disabled = false,
  isExporting = false,
}) => {
  return (
    <div className="flex items-center gap-3">
      {/* Print to PDF */}
      <button
        onClick={onPrintPDF}
        disabled={disabled || isExporting}
        className={`
          inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
          transition-colors
          ${disabled || isExporting
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }
        `}
        title={disabled ? 'Run the report first to enable export' : 'Print report to PDF'}
      >
        <PrinterIcon className="h-4 w-4 mr-2" />
        Print to PDF
      </button>

      {/* Export to Excel */}
      <button
        onClick={onExportExcel}
        disabled={disabled || isExporting}
        className={`
          inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
          transition-colors
          ${disabled || isExporting
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-green-600 text-white hover:bg-green-700'
          }
        `}
        title={disabled ? 'Run the report first to enable export' : 'Export report to Excel (CSV)'}
      >
        {isExporting ? (
          <>
            <svg
              className="animate-spin h-4 w-4 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Exporting...
          </>
        ) : (
          <>
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            Export to Excel
          </>
        )}
      </button>
    </div>
  );
};
